// @TODO [CX-663]: clean up this file when workspaces are removed

import { useTranslation } from 'react-i18next';
import { PLAYBACKS } from '@hotjar/webapp/constants/routes';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { sanitizeUUID } from '@hotjar/webapp/lib/sanitize';
import { getMinutesAndSeconds } from '@hotjar/webapp/lib/getMinutesAndSeconds';
import { useCurrentSite } from '@hotjar/site';
import type { Recording } from '@hotjar/webapp/domains/recordings/types';
import styled from '@emotion/styled';
import { tokens, Text, Icon, Loader, Button } from '@hotjar/hotsauce';
import { RunningStateIcon } from '@hotjar/webapp/components/RunningStateIcon';
import { Card } from '@hotjar/webapp/components/Card';
import { useSamplingState } from '@hotjar/webapp/hooks/useSamplingState';
import type { Segment } from '@hotjar/webapp/features/Recordings/types';
import { WidgetGhostButton } from '../WidgetGhostButton';
import { NewEmptyWidget, EMPTY_STATE_KIND, EMPTY_STATE_TOOL } from '../NewEmptyWidget';

const RecordingsList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  list-styled: none;
  padding: 0;
  margin: 0;
`;

const RecordingItem = styled.li`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  align-items: center;
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
  box-shadow: inset 0px -1px 0px ${tokens.color.neutral_100};
  background-color: ${tokens.color.ui_background};

  &:last-of-type {
    border-bottom: 0;
  }

  &:hover {
    cursor: pointer;
    background-color: ${tokens.color.bg_hover};
    transition: background-color 300ms linear;
  }
`;

const Flag = styled.span`
  width: 16px;
  height: 11px;
  margin-right: ${tokens.spacer.x1};
`;

const Cell = styled.div`
  text-align: center;
  ${tokens.text('caption')};
  &:first-of-type {
    text-align: left;
  }
  &:last-of-type {
    text-align: right;
  }
`;

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  margin: 0 auto;
`;

const EmptyText = styled(Text)`
  color: ${tokens.color.text_light};
  margin: ${tokens.spacer.x2} 0 ${tokens.spacer.x0};
`;

const StyledLoader = styled(Loader)`
  padding: ${tokens.spacer.x4};
`;

const ListFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: ${tokens.spacer.x1pt5};
`;

const StyledIcon = styled(Icon)`
  color: ${tokens.color.text_light};
`;

interface RecordingsCardListProps {
  recordingsList?: Recording[];
  selectedSegmentId?: Segment['id'];
  onFooterButtonClick: () => void;
  onRecordingsClick: (recording: Recording, i: number) => void;
}

export const RecordingsCardList = ({
  recordingsList,
  selectedSegmentId,
  onRecordingsClick,
  onFooterButtonClick,
}: RecordingsCardListProps): JSX.Element => {
  const { t } = useTranslation();

  const { data: currentSite } = useCurrentSite();
  const { isLoading: isSamplingStateLoading, inDisabledState, inWarningState } = useSamplingState();

  // default to empty state when not is loading and recordings list is undefined
  // this is an error scenario
  if (!recordingsList) {
    return (
      <Card.Body>
        <EmptyWrapper>
          <RunningStateIcon />
          <EmptyText textType="body2">
            <span>{t('activation:dashboard.recordings-card.empty-text-pending-line1')}</span>
            <br />
            <span>{t('activation:dashboard.recordings-card.empty-text-pending-line2')}</span>
          </EmptyText>
        </EmptyWrapper>
      </Card.Body>
    );
  }

  const playbacksListPath = `${getRoute(PLAYBACKS.LIST.path, {
    siteId: currentSite?.id as number,
  })}?sort_by=-relevance_score`;

  if (recordingsList?.length === 0) {
    if (isSamplingStateLoading) {
      return (
        <Card.Body>
          <EmptyWrapper>
            <StyledLoader />
          </EmptyWrapper>
        </Card.Body>
      );
    }

    if (inDisabledState || inWarningState) {
      return <NewEmptyWidget tool={EMPTY_STATE_TOOL.recordings} kind={EMPTY_STATE_KIND.ERROR} />;
    }

    if (selectedSegmentId !== -1) {
      return (
        <Card.Body>
          <EmptyWrapper>
            <StyledIcon icon="warning" size="l" />
            <EmptyText style={{ marginBottom: tokens.spacer.x2 }} textType="body2">
              <span>{t('activation:dashboard.recordings-card.spaces-empty-segment-line1')}</span>
            </EmptyText>
            <Button kind="secondary" as="a" href={playbacksListPath}>
              {t('activation:dashboard.recordings-card.go-to-recordings')}
            </Button>
          </EmptyWrapper>
        </Card.Body>
      );
    }

    return <NewEmptyWidget kind={EMPTY_STATE_KIND.EMPTY} tool={EMPTY_STATE_TOOL.recordings} />;
  }

  return currentSite?.id ? (
    <>
      <RecordingsList>
        {recordingsList.map((recording, i) => (
          <RecordingItem
            role="button"
            onClick={() => onRecordingsClick(recording, i)}
            key={recording.id}
          >
            <Cell>
              <Flag className={`flag flag-${(recording.countryCode || '').toLowerCase()}`} />
              {sanitizeUUID(recording.visitorUuid)}
            </Cell>
            <Cell>{`${recording.actionCount} Actions`}</Cell>
            <Cell>{`${getMinutesAndSeconds(recording.duration)} min`}</Cell>
            <Cell>
              <WidgetGhostButton
                data-testid={`cta_${recording.id}`}
                size="s"
                onClick={() => onRecordingsClick(recording, i)}
              >
                {t('activation:dashboard.recordings-card.play-button')}
              </WidgetGhostButton>
            </Cell>
          </RecordingItem>
        ))}
      </RecordingsList>
      <ListFooter>
        <WidgetGhostButton as="a" href={playbacksListPath} onClick={() => onFooterButtonClick()}>
          {t('activation:dashboard.recordings-card.footer')}
        </WidgetGhostButton>
      </ListFooter>
    </>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );
};
